/* ============================================
   Configuración → General: apariencia e integraciones

   Es lo que vivía en Ajustes, pasado al design system: tarjetas `.card`,
   badges e íconos de `Icons.tsx`. Tenía estilos propios (`partner-ajustes__*`),
   un `<h1>` y emojis como estado (🟢/🔴/❌/✅), que se ven distinto en cada
   sistema operativo y no heredan el color del texto.
   ============================================ */
import { useCallback, useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { useTheme } from '@/hooks/useTheme'
import { Badge } from '@/components/ui'
import { IconMoon, IconSun, IconEye, IconEyeOff, IconCheck, IconAlertCircle } from '@/components/icons/Icons'

interface WazzupConfig {
  id: number
  channel_id: string
  channel_name: string | null
  phone_number: string | null
  estado: string
  wazzup_api_key_masked: string
  last_validated_at: string | null
}

interface WazzupChannel {
  id: string
  name: string
  transport: string
  state: string
  phone?: string
}

const WEBHOOK = 'https://api.1conecta.com/api/webhooks/wazzup'

export default function PartnerConfigGeneral() {
  const api = useApi()
  const [tema, alternarTema] = useTheme()

  const [config, setConfig] = useState<WazzupConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [validating, setValidating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [apiKey, setApiKey] = useState('')
  const [channelId, setChannelId] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  const [channels, setChannels] = useState<WazzupChannel[]>([])
  const [validated, setValidated] = useState(false)

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true)
      const data = await api.get<WazzupConfig & { error?: string }>('/api/partner/wazzup-config')
      if (data && !data.error) {
        setConfig(data)
        setChannelId(data.channel_id || '')
      } else {
        setConfig(null)
      }
    } catch {
      setConfig(null)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { loadConfig() }, [loadConfig])

  async function validar() {
    if (!apiKey.trim()) { setError('Ingresa tu API Key de Wazzup'); return }
    setError(null)
    setValidating(true)
    setChannels([])
    setValidated(false)
    try {
      const data = await api.post<{ channels?: WazzupChannel[]; error?: string }>(
        '/api/partner/wazzup-config/validate', { wazzup_api_key: apiKey.trim() },
      )
      if (data.error) { setError(data.error); return }

      const waChannels = (data.channels || []).filter(ch => ch.transport === 'whatsapp' && ch.state === 'active')
      if (waChannels.length === 0) {
        setError('No se encontraron canales de WhatsApp activos. Revisa que tu WhatsApp esté conectado en Wazzup.')
        return
      }
      setChannels(waChannels)
      setValidated(true)
      if (!channelId) setChannelId(waChannels[0].id)
      setSuccess('API Key válida. Ahora elige el canal.')
      window.setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.message || 'No se pudo validar')
    } finally { setValidating(false) }
  }

  async function guardar() {
    if (!apiKey.trim() && !config) { setError('Ingresa tu API Key'); return }
    if (!channelId) { setError('Elige un canal de WhatsApp'); return }
    setError(null)
    setSaving(true)
    try {
      const payload: Record<string, string> = { channel_id: channelId }
      if (apiKey.trim()) payload.wazzup_api_key = apiKey.trim()
      const data = await api.put<WazzupConfig & { error?: string }>('/api/partner/wazzup-config', payload)
      if (data.error) { setError(data.error); return }
      setConfig(data)
      setApiKey('')
      setChannels([])
      setValidated(false)
      setSuccess('Integración guardada')
      window.setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.message || 'No se pudo guardar')
    } finally { setSaving(false) }
  }

  async function desconectar() {
    if (!window.confirm('¿Desconectar tu integración de WhatsApp?')) return
    try {
      await api.del('/api/partner/wazzup-config')
      setConfig(null)
      setApiKey('')
      setChannelId('')
      setChannels([])
      setValidated(false)
      setSuccess('Integración desconectada')
      window.setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.message || 'No se pudo desconectar')
    }
  }

  if (loading) return <p className="pp-cargando">Cargando configuración…</p>

  return (
    <>
      {/* Apariencia. Estaba en los tres puntos del pie, junto a Cerrar sesión:
          un ajuste de la cuenta escondido en el menú de salir. */}
      <div className="card pp-card">
        <div className="pp-card__head">
          <div>
            <h3 className="pp-card__titulo">Apariencia</h3>
            <p className="pp-card__sub">Elige cómo se ve el panel.</p>
          </div>
        </div>
        <div className="pp-temas">
          {/* Cada botón nombra SU tema y se marca el activo, al revés que el
              interruptor del pie del ERP, que nombra el tema al que va: acá hay
              lugar para los dos y una opción elegida se lee de un golpe. */}
          <button
            type="button"
            className={`pp-tema${tema === 'light' ? ' pp-tema--activo' : ''}`}
            onClick={() => { if (tema !== 'light') alternarTema() }}
            aria-pressed={tema === 'light'}
          >
            <IconSun size={16} /> Claro
          </button>
          <button
            type="button"
            className={`pp-tema${tema === 'dark' ? ' pp-tema--activo' : ''}`}
            onClick={() => { if (tema !== 'dark') alternarTema() }}
            aria-pressed={tema === 'dark'}
          >
            <IconMoon size={16} /> Oscuro
          </button>
        </div>
      </div>

      <div className="card pp-card">
        <div className="pp-card__head">
          <div>
            <h3 className="pp-card__titulo">WhatsApp</h3>
            <p className="pp-card__sub">
              Conecta tu cuenta de Wazzup para enviar y recibir los mensajes de tus clientes
              desde este panel.
            </p>
          </div>
          {config ? (
            <Badge variant={config.estado === 'activo' ? 'aceptada' : 'rechazada'}>
              {config.estado === 'activo' ? 'Conectado' : 'Desconectado'}
            </Badge>
          ) : (
            <Badge variant="neutral">Sin conectar</Badge>
          )}
        </div>

        {config ? (
          <div className="pp-datos-filas">
            <div className="pp-fila-dato">
              <span>API Key</span>
              <code>{config.wazzup_api_key_masked || '—'}</code>
            </div>
            <div className="pp-fila-dato">
              <span>Canal</span>
              <strong>{config.channel_name || config.channel_id || '—'}</strong>
            </div>
            {config.phone_number && (
              <div className="pp-fila-dato">
                <span>Número</span>
                <strong>{config.phone_number}</strong>
              </div>
            )}
            <div className="pp-card__pie">
              <button className="btn btn-danger" onClick={desconectar}>Desconectar</button>
            </div>
          </div>
        ) : (
          <div className="pp-wazzup">
            <ol className="pp-pasos">
              <li>Crea una cuenta en <a href="https://wazzup24.com" target="_blank" rel="noopener noreferrer">wazzup24.com</a>.</li>
              <li>Conecta tu WhatsApp escaneando el código QR.</li>
              <li>Entra a <strong>Configuración → API</strong> y copia tu API Key.</li>
              <li>Pégala aquí abajo y valida la conexión.</li>
            </ol>

            <div className="form-group">
              <label className="form-label">API Key de Wazzup</label>
              <div className="pp-clave-fila">
                <input
                  className="form-input"
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={e => { setApiKey(e.target.value); setValidated(false) }}
                  placeholder="Tu API Key de Wazzup"
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowApiKey(v => !v)}
                  title={showApiKey ? 'Ocultar' : 'Ver'}
                >
                  {showApiKey ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={validar}
                  disabled={validating || !apiKey.trim()}
                >
                  {validating ? 'Validando…' : 'Validar'}
                </button>
              </div>
            </div>

            {validated && channels.length > 0 && (
              <div className="form-group">
                <label className="form-label">Canal de WhatsApp</label>
                <select className="form-input" value={channelId} onChange={e => setChannelId(e.target.value)}>
                  <option value="">Elige un canal</option>
                  {channels.map(ch => (
                    <option key={ch.id} value={ch.id}>
                      {ch.name}{ch.phone ? ` (${ch.phone})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {validated && (
              <div className="pp-webhook">
                <strong>Último paso: el webhook</strong>
                <p>En tu cuenta de Wazzup, configura esta URL como webhook. Sin eso los mensajes de tus clientes no llegan acá.</p>
                <code>{WEBHOOK}</code>
              </div>
            )}

            {validated && channelId && (
              <div className="pp-card__pie">
                <button className="btn btn-primary" onClick={guardar} disabled={saving}>
                  {saving ? 'Guardando…' : 'Guardar integración'}
                </button>
              </div>
            )}
          </div>
        )}

        {error && <p className="pp-aviso pp-aviso--error"><IconAlertCircle size={14} /> {error}</p>}
        {success && <p className="pp-aviso pp-aviso--ok"><IconCheck size={14} /> {success}</p>}
      </div>
    </>
  )
}
